import React, {createContext, useState, useEffect, ReactElement} from 'react'
import Script from 'next/script'
import {useRouter} from 'next/router'

import {SplitIoProvider} from '@miro-site/splitio'
import {eventHub, getAnalytics} from '@miro-site/analytics'
import type {EventHubMethods, Analytics} from '@miro-site/analytics'

export interface ServicesContextData {
  analytics: Analytics | undefined
  eventHub: EventHubMethods | undefined
}

export const ServicesContext = createContext<ServicesContextData>({
  analytics: undefined,
  eventHub: undefined,
})

interface ServicesProviderProps {
  children: ReactElement
  withAnalytics?: boolean
  withSplitIo?: boolean
  withEventHub?: boolean
}

const userActions = ['mousemove', 'keydown', 'click', 'touchstart']

export const ServicesProvider = ({
  children,

  withAnalytics = true,
  withEventHub = true,
  withSplitIo = true,
}: ServicesProviderProps): ReactElement => {
  const router = useRouter()
  const [oneTrustIsLoaded, setOneTrustIsLoaded] = useState(false)

  const [loadOneTrust, setLoadOneTrust] = useState(false)
  const [loadAnalytics, setLoadAnalytics] = useState(false)

  // Directly load Split IO to avoid screen flickering with experiments above the fold
  const loadSplitIo = withSplitIo && !!process.env.NEXT_PUBLIC_SPLITIO_CLIENT_KEY

  // GTM
  const analytics = getAnalytics()

  // Add listener to trigger "page view" at route change
  useEffect(() => {
    if (!withEventHub || !eventHub) {
      return
    }

    // Trigger page view for initial load
    eventHub.page()

    // Setup callback for route changes
    router.events.on('routeChangeComplete', eventHub.page)

    // Remove event tracking when provider is unmounted
    return () => {
      router.events.off('routeChangeComplete', eventHub.page)
    }
  }, [withEventHub, eventHub])

  // Add listener to load `OneTrust`
  useEffect(() => {
    for (const action of userActions) {
      window.addEventListener(action, handleUserAction)
    }

    // Return clean-up function
    return removeInteractionListeners
  }, [])

  // When One Trust is loaded, enable analytics to be loaded
  useEffect(() => {
    if (!oneTrustIsLoaded) {
      return
    }

    setLoadAnalytics(withAnalytics)
  }, [oneTrustIsLoaded])

  // Initialize analytics providers
  useEffect(() => {
    if (loadAnalytics && analytics) {
      analytics.initialize()
    }
  }, [loadAnalytics, analytics])

  // Handler initial user action
  const handleUserAction = _e => {
    // Enable One Trust script to be loaded
    setLoadOneTrust(true)

    // Remove listeners to avoid repeating this function
    removeInteractionListeners()
  }

  // Clean up function to remove the user action listeners
  const removeInteractionListeners = () => {
    for (const action of userActions) {
      window.removeEventListener(action, handleUserAction)
    }
  }

  const handleOneTrustOnLoad = () => {
    // Update flag to load other scripts only when OneTrust is initialized
    const checkOneTrustInit = setInterval(() => {
      if (window.OneTrust) {
        setOneTrustIsLoaded(true)
        window.dispatchEvent(new CustomEvent('oneTrustLoaded'))

        // Clear interval to stop looping
        clearInterval(checkOneTrustInit)
      }
    }, 300)
  }

  let content = (
    <>
      <Script
        strategy="beforeInteractive"
        // Removing next/script from first render negatively impacts performance.
        // We are explicitly using miro's host to load optanonWrapper using next/script.
        src="https://miro.com/scripts/optanonWrapper.js"
      />

      {loadOneTrust && (
        <Script
          src="https://cdn.cookielaw.org/consent/4133a0ef-6999-461d-909d-24f2b05bbc15/otSDKStub.js"
          data-document-language="true"
          charSet="UTF-8"
          data-domain-script="4133a0ef-6999-461d-909d-24f2b05bbc15"
          onLoad={handleOneTrustOnLoad}
        />
      )}
      {children}
    </>
  )

  if (loadSplitIo) {
    content = <SplitIoProvider>{content}</SplitIoProvider>
  }

  return (
    <ServicesContext.Provider value={{analytics, eventHub}}>{content}</ServicesContext.Provider>
  )
}
